import { inject, observer } from 'mobx-react';
import React from 'react';
import {
  Button,
  FormLabel,
  Form,
  FormControl,
  FormGroup,
  Modal,
  Card,
  Row,
  Col,
  Badge,
  Dropdown,
  OverlayTrigger,
  Tooltip,
  Nav,
  Alert,
} from 'react-bootstrap';
import { RouteComponentProps, withRouter, NavLink } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment-mini';

import ModelItem, { ModelType } from '../../models/ModelItem';
import { PROFILES_PERMISSIONS, checkIsArchitect } from '../../permissions';
import Store from '../../store';
import { canSeeDataStudio, isV2User } from '../utils/rbac';
import checkPermissions from '../components/CheckPermissions';
import Error from '../components/error/Error';
import ModelStatus from '../components/ModelStatus';
import PageHeader from '../components/PageHeader';
import DuplicationModelModal from '../components/modals/DuplicationModelModal';
import partition from 'lodash/partition';
import Table from '../components/generic_components/Table';
import CustomToggle from '../components/generic_components/CustomToggle';
import { MODEL_TYPES_NAMES } from '../../utils/constants';

type OperationType = 'create' | 'edit';
const CREATE: OperationType = 'create';
const EDIT: OperationType = 'edit';

type MatchParams = {
  tenant: string;
};

interface TenantPageProps extends RouteComponentProps<MatchParams> {
  store?: Store;
}

interface TenantPageState {
  duplicateModelModalIsShown: boolean;
  updateModelNameModalIsShown: boolean;
  modelBeingUpdatedId: number;
  modelBeingUpdatedName: string;
  fromModel: number;
  newModelName: string;
  newModelType: ModelType;
  saving: boolean;
  showArchivedModels: boolean;
  activeModel?: ModelItem;
  showArchiveModal: boolean;
  showDeactivateStandardFitModal: boolean;
}

const TenantPage = inject('store')(
  observer(
    class TenantPage extends React.Component<TenantPageProps, TenantPageState> {
      constructor(props: TenantPageProps) {
        super(props);

        this.state = {
          duplicateModelModalIsShown: false,
          updateModelNameModalIsShown: false,
          modelBeingUpdatedId: null,
          modelBeingUpdatedName: '',
          fromModel: null,
          newModelName: '',
          newModelType: 'customer_fit',
          saving: false,
          showArchivedModels: false,
          showArchiveModal: false,
          showDeactivateStandardFitModal: false,
        };

        this.closeModal = this.closeModal.bind(this);
        this.openDuplicateModelModal = this.openDuplicateModelModal.bind(this);
        this.handleModelNameChange = this.handleModelNameChange.bind(this);
        this.handleModelTypeChange = this.handleModelTypeChange.bind(this);
        this.createNewModelAndRedirect =
          this.createNewModelAndRedirect.bind(this);
        this.updateModelName = this.updateModelName.bind(this);
        this.openUpdateModelNameModal =
          this.openUpdateModelNameModal.bind(this);
        this.toggleShowArchivedModels =
          this.toggleShowArchivedModels.bind(this);
        this.toggleArchiveModal = this.toggleArchiveModal.bind(this);
        this.handleArchiveButtonClick =
          this.handleArchiveButtonClick.bind(this);
        this.toggleDeactivateStandardFitModal =
          this.toggleDeactivateStandardFitModal.bind(this);
        this.handleDeactivateStandardFitButtonClick =
          this.handleDeactivateStandardFitButtonClick.bind(this);
      }

      async componentDidMount() {
        await this.props.store.getTenantModels();
      }

      closeModal() {
        this.setState({
          duplicateModelModalIsShown: false,
          updateModelNameModalIsShown: false,
          modelBeingUpdatedId: null,
          modelBeingUpdatedName: '',
          newModelName: '',
          saving: false,
        });
      }

      openDuplicateModelModal(fromModel: number) {
        this.setState({ fromModel, duplicateModelModalIsShown: true });
      }

      handleModelNameChange(newValue: string, operation: OperationType) {
        if (operation === CREATE) this.setState({ newModelName: newValue });
        if (operation === EDIT)
          this.setState({ modelBeingUpdatedName: newValue });
      }

      handleModelTypeChange(newValue: ModelType) {
        this.setState({ newModelType: newValue });
      }

      toggleShowArchivedModels() {
        this.setState({ showArchivedModels: !this.state.showArchivedModels });
      }

      async createNewModelAndRedirect() {
        this.setState({
          saving: true,
        });

        const { newModelName, newModelType } = this.state;
        const { tenant } = this.props.store;
        const newModelId: number = await this.props.store.rootPage.createModel(
          tenant,
          { name: newModelName, type: newModelType }
        );

        window.location.href = `/tenant/${this.props.match.params.tenant}/models/${newModelId}`;
      }

      openUpdateModelNameModal(modelId: number, modelName: string) {
        this.setState({
          updateModelNameModalIsShown: true,
          modelBeingUpdatedId: modelId,
          modelBeingUpdatedName: modelName,
        });
      }

      async updateModelName(modelId: number, name: string) {
        await this.props.store.updateModelName(modelId, name);
        window.location.reload();
      }

      async updateModelArchiveStatus(modelId: number, isArchived: boolean) {
        await this.props.store.updateModelArchiveStatus(modelId, isArchived);
      }

      toggleArchiveModal() {
        this.setState({ showArchiveModal: !this.state.showArchiveModal });
      }

      toggleDeactivateStandardFitModal() {
        this.setState({
          showDeactivateStandardFitModal:
            !this.state.showDeactivateStandardFitModal,
        });
      }

      async handleArchiveButtonClick() {
        await this.updateModelArchiveStatus(
          this.state.activeModel.modelId,
          !this.state.activeModel.is_archived
        );
      }

      async handleDeactivateStandardFitButtonClick() {
        await this.props.store.deactivateStandardFitModel(
          this.state.activeModel.modelId
        );
      }

      render() {
        const {
          tenantModels,
          rootPage,
          loadingTenantModels,
          tenant: tenantId,
          rbac,
          isSuperKudu,
          userPermissions,
        } = this.props.store;
        const {
          createModelError,
          updateModelNameError,
          updateModelArchiveStatusError,
          updatingModelName,
          archivingModel,
          deactivateStandardFitError,
          deactivatingStandardFit,
        } = rootPage;
        const {
          duplicateModelModalIsShown,
          updateModelNameModalIsShown,
          modelBeingUpdatedId,
          modelBeingUpdatedName,
          fromModel,
          showArchivedModels,
        } = this.state;

        const isEditedModelNameValid = modelBeingUpdatedName.trim().length > 0;

        const [liveModels, draftModels] = partition(
          tenantModels,
          ({ live }) => live
        );

        // Who can create a model. RBAC v2 (Standard/Org Admin) gates on the
        // `data_studio` module assignment — matching the route gate in
        // StudioPage — while legacy (v1) users keep the architect/admin rule.
        // SuperKudu always bypasses.
        const canCreateModel = isSuperKudu
          ? true
          : isV2User(rbac)
          ? canSeeDataStudio(rbac)
          : checkIsArchitect(tenantId, userPermissions);
        const liveStandardFitModels = tenantModels
          .filter(
            ({ type, live, is_primary }) =>
              type === 'customer_fit' && live && is_primary
          )
          .sort((firstModel, secondModel) => {
            const firstTimestamp = firstModel.lastDeployedAt
              ? moment(firstModel.lastDeployedAt).valueOf()
              : 0;
            const secondTimestamp = secondModel.lastDeployedAt
              ? moment(secondModel.lastDeployedAt).valueOf()
              : 0;

            return firstTimestamp - secondTimestamp;
          });
        const liveStandardFitModelIds = new Set(
          liveStandardFitModels.map(({ modelId }) => modelId)
        );
        const hasDuplicateLiveStandardFits = liveStandardFitModelIds.size > 1;
        const selectedLiveStandardFitIndex = liveStandardFitModels.findIndex(
          ({ modelId }) => modelId === this.state.activeModel?.modelId
        );
        const duplicateRankLabel =
          selectedLiveStandardFitIndex === -1
            ? null
            : selectedLiveStandardFitIndex === 0
            ? 'Oldest live standard fit'
            : selectedLiveStandardFitIndex === liveStandardFitModels.length - 1
            ? 'Newest live standard fit'
            : 'Intermediate live standard fit';

        const theDraftModels = !showArchivedModels
          ? draftModels.filter(({ is_archived }) => !is_archived)
          : draftModels;

        const { tenant } = this.props.match.params;

        const columnHelper = createColumnHelper<ModelItem>();

        const columns: ColumnDef<ModelItem, unknown>[] = [
          columnHelper.accessor('modelId', {
            cell: (info) => info.getValue(),
            header: 'Model ID',
          }),
          {
            accessorFn: ({ type }) => MODEL_TYPES_NAMES[type],
            id: 'type',
            header: 'Model Type',
            cell: (info) => info.getValue(),
          },
          columnHelper.accessor('name', {
            cell: (info) => {
              const value = info.getValue();
              const { modelId } = info.row.original;
              return (
                <a href={`/tenant/${tenant}/models/${modelId}`}>{value}</a>
              );
            },
            header: 'Model Name',
          }),
          {
            accessorFn: (row): string => {
              const scoringTypes = [];
              if (row.realtime) scoringTypes.push('real-time');
              if (row.batch) scoringTypes.push('batch');

              return scoringTypes.join(' and ') || '-';
            },
            id: 'scoringType',
            header: 'Scoring Type',
            cell: (info) => {
              const value = info.getValue() as string;

              return (
                <>
                  {value.includes('real-time') && (
                    <Badge bg="secondary" className="ms-1 me-1">
                      Real-time
                    </Badge>
                  )}
                  {value.includes('batch') && (
                    <Badge bg="secondary" className="ms-1 me-1">
                      Batch
                    </Badge>
                  )}
                </>
              );
            },
            sortingFn: (rowA, rowB) => {
              const getScore = (row: ModelItem) => {
                let score = 0;
                if (row.batch) score += 2;
                if (row.realtime) score += 1;

                return score;
              };

              const scoreA = getScore(rowA.original);
              const scoreB = getScore(rowB.original);

              return scoreA - scoreB;
            },
            filterFn: ({ original: { realtime, batch } }, _, filterValue) => {
              if (filterValue === 'real-time') return realtime;
              if (filterValue === 'batch') return batch;
              if (filterValue === 'real-time and batch')
                return realtime && batch;

              return !realtime && !batch;
            },
          },
          {
            accessorFn: (row): string => {
              if (row.live) return 'live';
              if (row.is_archived) return 'archived';

              return '-';
            },
            id: 'status',
            header: 'Status',
            cell: (info) => {
              const value = info.getValue();

              if (value === 'live')
                return <ModelStatus model={info.row.original} />;

              if (value === 'archived')
                return <Badge bg="secondary">Archived</Badge>;

              return <Badge bg="secondary">Draft</Badge>;
            },
            sortingFn: (rowA, rowB) => {
              const getScore = (row: ModelItem) => {
                let score = 0;
                if (row.live) score += 2;
                if (row.is_archived) score += 1;

                return score;
              };

              const scoreA = getScore(rowA.original);
              const scoreB = getScore(rowB.original);

              return scoreA - scoreB;
            },
            filterFn: ({ original: { live, is_archived } }, _, filterValue) => {
              if (filterValue === 'live') return live;
              if (filterValue === 'archived') return is_archived;

              return !live && !is_archived;
            },
          },
          {
            accessorFn: (row) =>
              // Data discovery dag keeps updating models when they are live.
              // As a workaround we show last deployed at date for live models.
              row.live ? row.lastDeployedAt : row.updatedAt,
            cell: (info) => moment(info.getValue()).format('ll'),
            id: 'updatedAt',
            header: 'Updated at',
            enableColumnFilter: false,
          },
          {
            accessorFn: (row) => {
              return checkPermissions(
                PROFILES_PERMISSIONS.ARCHITECT,
                <td>
                  <Dropdown>
                    <Dropdown.Toggle as={CustomToggle}></Dropdown.Toggle>
                    <Dropdown.Menu>
                      <OverlayTrigger
                        trigger="hover"
                        placement="left"
                        defaultShow={row.live}
                        overlay={
                          <Tooltip id="rename-action">
                            Live models cannot be renamed
                          </Tooltip>
                        }
                      >
                        <Dropdown.Item
                          onClick={() => {
                            this.openUpdateModelNameModal(
                              row.modelId,
                              row.name
                            );
                          }}
                          disabled={row.live}
                          style={{ pointerEvents: 'visible' }}
                        >
                          Rename
                        </Dropdown.Item>
                      </OverlayTrigger>
                      <Dropdown.Item
                        onClick={() => {
                          this.openDuplicateModelModal(row.modelId);
                        }}
                      >
                        Duplicate
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      {row.type === 'customer_fit' &&
                        row.live &&
                        row.is_primary &&
                        this.props.store.isSuperKudu &&
                        hasDuplicateLiveStandardFits && (
                          <>
                            <Dropdown.Item
                              onClick={() => {
                                this.setState({
                                  activeModel: row,
                                });
                                this.toggleDeactivateStandardFitModal();
                              }}
                            >
                              Deactivate primary fit
                            </Dropdown.Item>
                            <Dropdown.Divider />
                          </>
                        )}
                      <OverlayTrigger
                        trigger="hover"
                        placement="left"
                        defaultShow={row.live}
                        overlay={
                          <Tooltip id="archive-action">
                            Live models cannot be archived
                          </Tooltip>
                        }
                      >
                        <Dropdown.Item
                          onClick={() => {
                            this.setState({
                              activeModel: row,
                            });
                            this.toggleArchiveModal();
                          }}
                          disabled={row.live}
                          style={{ pointerEvents: 'visible' }}
                        >
                          {row.is_archived ? 'Un-archive' : 'Archive'}
                        </Dropdown.Item>
                      </OverlayTrigger>
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              );
            },
            id: 'actions',
            enableSorting: false,
            header: 'Actions',
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
          },
        ];

        const liveColumns: ColumnDef<ModelItem, unknown>[] = columns.map(
          (column) => ({ ...column, enableColumnFilter: false })
        );

        const updateModelNameModal = (
          <Modal onHide={this.closeModal} show={updateModelNameModalIsShown}>
            <Modal.Header>
              <Modal.Title>Rename model</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <FormGroup>
                  <FormLabel className="fw-bold">Model name</FormLabel>
                  <FormControl
                    type="text"
                    value={modelBeingUpdatedName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      this.handleModelNameChange(event.target.value, EDIT)
                    }
                  />
                </FormGroup>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-primary" onClick={this.closeModal}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await this.updateModelName(
                    modelBeingUpdatedId,
                    modelBeingUpdatedName
                  );
                }}
                disabled={updatingModelName || !isEditedModelNameValid}
              >
                {updatingModelName ? 'Saving...' : 'Save'}
              </Button>
            </Modal.Footer>
          </Modal>
        );

        return (
          <>
            {updateModelNameModal}
            <DuplicationModelModal
              fromModel={fromModel}
              close={this.closeModal}
              isShown={duplicateModelModalIsShown}
            />
            <PageHeader>
              <Row>
                <Col>
                  <h2
                    style={{ fontWeight: 700, margin: 0 }}
                    id="page-header-models"
                  >
                    Models
                  </h2>
                </Col>
                <Col className="text-end">
                  {canCreateModel && (
                    <Nav.Link as={NavLink} to={`/tenant/${tenant}/models/new`}>
                      <Button variant="primary" id="create-new-model-button">
                        <i className="fas fa-plus" /> Create new model
                      </Button>
                    </Nav.Link>
                  )}
                </Col>
              </Row>
            </PageHeader>
            <div className="mt-3 mb-5">
              {createModelError && <Error message={createModelError} />}
              {updateModelNameError && <Error message={updateModelNameError} />}
              {updateModelArchiveStatusError && (
                <Error message={updateModelArchiveStatusError} />
              )}
              {deactivateStandardFitError && (
                <Error message={deactivateStandardFitError} />
              )}

              {!!liveModels.length && (
                <>
                  <h3
                    className="mt-4"
                    style={{
                      fontWeight: 700,
                      fontSize: '16px',
                      marginBottom: '12px',
                    }}
                  >
                    My live models
                  </h3>
                  <Card>
                    <Card.Body>
                      <Table data={liveModels} columns={liveColumns} />
                    </Card.Body>
                  </Card>
                </>
              )}

              {!!theDraftModels.length && (
                <>
                  <Row className="mt-4">
                    <Col>
                      <h3
                        style={{
                          fontWeight: 700,
                          fontSize: '16px',
                          marginBottom: '12px',
                        }}
                      >
                        My draft models
                      </h3>
                    </Col>
                    <Col className="d-flex justify-content-end">
                      <Form.Check
                        id="switch-archived"
                        type="switch"
                        label="Show archived models"
                        checked={showArchivedModels}
                        onChange={this.toggleShowArchivedModels}
                      />
                    </Col>
                  </Row>

                  <Card>
                    <Card.Body>
                      <Table
                        data={theDraftModels}
                        columns={columns}
                        defaultSorting={[{ id: 'updatedAt', desc: true }]}
                        defaultPageSize={50}
                      />
                    </Card.Body>
                  </Card>
                </>
              )}

              {!loadingTenantModels &&
                !liveModels.length &&
                !theDraftModels.length && (
                  <Alert variant="info" className="mt-4">
                    No models have been created for this tenant yet.
                    {canCreateModel &&
                      ' Click “Create new model” to get started.'}
                  </Alert>
                )}
            </div>

            <Modal
              show={this.state.showArchiveModal}
              onHide={this.toggleArchiveModal}
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  {this.state?.activeModel?.is_archived
                    ? 'Un-archive'
                    : 'Archive'}{' '}
                  model
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Are you sure you want to{' '}
                {this.state?.activeModel?.is_archived
                  ? 'un-archive'
                  : 'archive'}{' '}
                this model?
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={this.toggleArchiveModal}>
                  Close
                </Button>
                <Button
                  disabled={archivingModel}
                  variant="primary"
                  onClick={this.handleArchiveButtonClick}
                >
                  {this.state?.activeModel?.is_archived
                    ? 'Un-archive'
                    : 'Archive'}
                </Button>
              </Modal.Footer>
            </Modal>
            <Modal
              show={this.state.showDeactivateStandardFitModal}
              onHide={this.toggleDeactivateStandardFitModal}
            >
              <Modal.Header closeButton>
                <Modal.Title>Deactivate primary fit</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                This will turn off the live primary fit flag for{' '}
                <b>{this.state?.activeModel?.name}</b>.
                <br />
                <br />
                <div className="mb-3">
                  <div>
                    <b>Model</b>: {this.state?.activeModel?.name}
                  </div>
                  <div>
                    <b>Model ID</b>: {this.state?.activeModel?.modelId}
                  </div>
                  <div>
                    <b>Last deployed at</b>:{' '}
                    {this.state?.activeModel?.lastDeployedAt
                      ? moment(this.state.activeModel.lastDeployedAt).format(
                          'MMM DD, YYYY h:mma'
                        )
                      : '-'}
                  </div>
                  <div>
                    <b>Last deployed by</b>:{' '}
                    {this.state?.activeModel?.lastDeployedBy ?? '-'}
                  </div>
                  <div>
                    <b>Duplicate rank</b>: {duplicateRankLabel ?? '-'}
                  </div>
                </div>
                <p className="mb-3">
                  Usually you want to deactivate the older duplicate, not the
                  newest one.
                </p>
                This action is only available when another live primary fit
                already exists, so it fixes duplicate-primary cases without
                leaving the tenant with no primary fit model.
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={this.toggleDeactivateStandardFitModal}
                >
                  Close
                </Button>
                <Button
                  disabled={deactivatingStandardFit}
                  variant="primary"
                  onClick={this.handleDeactivateStandardFitButtonClick}
                >
                  Deactivate
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        );
      }
    }
  )
);

export default withRouter(TenantPage);
